import type { User } from '@auth0/auth0-react';
import type { Private } from '@seek/indie-api-types';
import { Stack } from 'braid-design-system';
import { useState } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { useLocation } from 'react-router-dom';

import { useApi } from 'src/api';
import { ErrorNotice } from 'src/components/ErrorNotice/ErrorNotice';
import { useLogin } from 'src/hooks/auth';

import type { FormValues } from '../../SecurityBreach';

import { ActionsTaken } from './components/ActionsTaken/ActionsTaken';
import { ContactInfo } from './components/ContactInfo/ContactInfo';
import { DataType } from './components/DataType/DataType';
import { ExtraDetails } from './components/ExtraDetails/ExtraDetails';
import { LoginPrompt } from './components/LoginPrompt/LoginPrompt';
import { ProductsImpacted } from './components/ProductsImpacted/ProductsImpacted';
import { SeekData } from './components/SeekData/SeekData';
import { StartDate } from './components/StartDate/StartDate';
import { Submit } from './components/Submit/Submit';
import { TerminationConfirmationDialog } from './components/TerminationConfirmation/TerminationConfirmation';
import { ThanksView } from './components/ThanksView/ThanksView';
import { UsersImpacted } from './components/UsersImpacted/UsersImpacted';

type SetBooleanState = React.Dispatch<React.SetStateAction<boolean>>;

type State = {
  setSubmitted: SetBooleanState;
  setOpenIntegrationTerminationDialog: SetBooleanState;
  setRequestError: SetBooleanState;
};

type Apis = {
  authenticated: ReturnType<typeof useApi>;
  unauthenticated: ReturnType<typeof useApi>;
};

const submit = async (
  values: FormValues,
  { setSubmitted, setOpenIntegrationTerminationDialog, setRequestError }: State,
  apis: Apis,
  terminateIntegration: boolean,
) => {
  const report: Private.SecurityBreachInput = {
    ...values,
    productsImpacted: [
      values.productsImpacted[0],
      ...values.productsImpacted.slice(1),
    ],
    incidentStartTimeUTC: getUTCDate(values.startDate),
    seekData: values.seekData === 'Yes',
    seekCredentials: values.seekCredentials === 'Yes',
    seekFault: values.seekFault === 'Yes',
    actionTaken: values.actionTaken === 'Yes',
    partnerInfo: {
      partnerName: values.partnerName,
      contactEmail: values.contactEmail,
      contactName: values.contactName,
      contactPhone: values.contactPhone,
      contactHours: values.contactHours,
    },
    numberImpacted: values.usersImpacted,
  };

  try {
    if (terminateIntegration) {
      await apis.authenticated.securityBreach.reportAuthenticated(report);
    } else {
      await apis.unauthenticated.securityBreach.reportUnauthenticated(report);
    }
    setSubmitted(true);
    setOpenIntegrationTerminationDialog(false);
  } catch (error) {
    setRequestError(true);
  }
};

const getUTCDate = (date: string) => {
  const dateObj = new Date(date);
  const utcDate = new Date(
    dateObj.getTime() + new Date().getTimezoneOffset() * 60000,
  );
  return utcDate.toISOString();
};

interface SecurityBreachFormProps {
  defaultValues: FormValues;
  user: User | undefined;
}

export const SecurityBreachForm = ({
  defaultValues,
  user,
}: SecurityBreachFormProps) => {
  const authenticated = useApi();
  const unauthenticated = useApi({ dangerouslyDefeatAuth: true });
  const [submitted, setSubmitted] = useState(false);
  const [
    openIntegrationTerminationDialog,
    setOpenIntegrationTerminationDialog,
  ] = useState(false);
  const [openLoginDialog, setOpenLoginDialog] = useState(false);
  const [requestError, setRequestError] = useState(false);
  const [confirmTermination, setConfirmTermination] = useState(false);

  const { login } = useLogin();
  const { pathname, search } = useLocation();

  const methods = useForm<FormValues>({
    defaultValues,
    reValidateMode: 'onChange',
  });
  const { handleSubmit, getValues } = methods;

  if (!user && !openLoginDialog) {
    setOpenLoginDialog(true);
  }

  const onValid = () => {
    if (terminateIntegration) {
      setOpenIntegrationTerminationDialog(true);
    } else {
      submit(
        getValues(),
        { setSubmitted, setOpenIntegrationTerminationDialog, setRequestError },
        { authenticated, unauthenticated },
        terminateIntegration,
      );
    }
  };

  const terminateIntegration: boolean =
    (getValues('seekData') === 'Yes' ||
      getValues('seekCredentials') === 'Yes' ||
      getValues('seekFault') === 'Yes') &&
    Boolean(user);

  return (
    <>
      {!submitted ? (
        <form onSubmit={handleSubmit(onValid)}>
          <Stack space="large">
            <FormProvider {...methods}>
              <Stack space="large">
                <DataType />
                <UsersImpacted />
                <StartDate />
                <ActionsTaken />
                <ProductsImpacted />
                <SeekData />
                <ContactInfo />
                <ExtraDetails />
              </Stack>
            </FormProvider>
            {requestError && (
              <ErrorNotice
                title="Error submitting request"
                description="There was a problem submitting your request. Try again."
              />
            )}
            <Submit terminateIntegration={terminateIntegration} />
          </Stack>
        </form>
      ) : (
        <ThanksView
          formValues={getValues()}
          terminateIntegration={terminateIntegration}
        />
      )}
      <TerminationConfirmationDialog
        open={openIntegrationTerminationDialog}
        setOpen={setOpenIntegrationTerminationDialog}
        confirmTermination={confirmTermination}
        setConfirmTermination={setConfirmTermination}
        submit={() =>
          submit(
            getValues(),
            {
              setSubmitted,
              setOpenIntegrationTerminationDialog,
              setRequestError,
            },
            { authenticated, unauthenticated },
            terminateIntegration,
          )
        }
        values={getValues()}
      />
      <LoginPrompt
        open={openLoginDialog}
        setOpen={setOpenLoginDialog}
        login={login}
        redirect={`${pathname}${search}`}
      />
    </>
  );
};
